import React, { useState } from "react";
import { toast } from "react-toastify";
import { PiMagicWandFill } from "react-icons/pi";
import { IoClose, IoSparkles } from "react-icons/io5";
import { FaCheck } from "react-icons/fa6";
import { useAiQuota } from "../Hooks/useAiQuota";
import { compressImageForAi } from "../../utils/compressImageForAi";
import { cleanCaptionSentence } from "../../utils/cleanCaptionSentence";
import axios from "../Hooks/axios";
import styles from "./AskFameoModal.module.scss";

const AskFameoAssistantModal = ({
  isOpen,
  onClose,
  mediaFile,
  onApplyCaption,
  initialPrompt = "",
  type = "post", // 'post' or 'story'
}) => {
  const { quota, setQuota, countdownText, isLimitReached } = useAiQuota();
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [tone, setTone] = useState("Trendy & Catchy");
  const [loading, setLoading] = useState(false);
  const [generatedResults, setGeneratedResults] = useState(null);

  React.useEffect(() => {
    if (isOpen) {
      if (initialPrompt) {
        setPrompt(initialPrompt);
      }
    }
  }, [isOpen, initialPrompt]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (isLimitReached) {
      toast.warn(`Daily limit reached. Resets in ${countdownText}`);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("tone", tone);
      formData.append("type", type);

      // Collect previously shown captions in modal or history to avoid repeats
      const seenCaptions = [
        ...(generatedResults?.captions || []).map((c) => cleanCaptionSentence(c.text || c)),
        ...(quota?.history || []).map((h) => cleanCaptionSentence(h.caption)),
      ].filter(Boolean);
      const uniquePrevious = Array.from(new Set(seenCaptions));
      if (uniquePrevious.length > 0) {
        formData.append("previousCaptions", JSON.stringify(uniquePrevious));
      }

      if (mediaFile) {
        let fileToUpload = mediaFile;
        // If it's a File or Blob, compress it to 512px to save Gemini tokens and upload time
        if (mediaFile instanceof Blob || mediaFile instanceof File) {
          fileToUpload = await compressImageForAi(mediaFile, 512);
          formData.append("image", fileToUpload);
        }
      }

      const { data } = await axios.post("/ask-fameo/generate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data?.status && data?.data) {
        setGeneratedResults(data.data);
        if (data.quota) {
          setQuota(data.quota);
        }
        if (data.isDemoMode) {
          toast.info("💡 Preview Mode: Add GEMINI_API_KEY in BE/.env for live Gemini generation.");
        } else {
          toast.success("✨ Captions created by Fameo AI!");
        }
      } else {
        toast.error(data?.message || "Failed to generate caption.");
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const quotaInfo = err.response.data.quota;
        if (quotaInfo) setQuota(quotaInfo);
        toast.error(
          err.response.data.message || "Daily limit reached (15/15)!"
        );
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to generate captions. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCaption = (captionText, hashtags = []) => {
    onApplyCaption(captionText, hashtags);
    toast.success("Caption applied to your " + (type === "story" ? "story!" : "post!"));
    onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.title}>
            <div className={styles.iconCircle}>
              <PiMagicWandFill className={styles.wandIcon} />
            </div>
            <h2>Ask Fameo-X</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <IoClose size={22} />
          </button>
        </div>

        {/* Live Quota & Countdown Banner */}
        <div
          className={`${styles.quotaBanner} ${
            isLimitReached ? styles.quotaExceeded : ""
          }`}
        >
          <div className={styles.quotaInfo}>
            <span>
              ⚡ AI Credits:{" "}
              <strong>
                {quota.remaining} / {quota.limit}
              </strong>{" "}
              left today
            </span>
          </div>
          {isLimitReached ? (
            <div className={styles.cooldownBadge}>
              ⏳ Available again in:{" "}
              <strong>{countdownText || "Calculating..."}</strong>
            </div>
          ) : (
            <span className={styles.tip}>Resets daily at 00:00 UTC</span>
          )}
        </div>

        {/* Media Preview info */}
        {mediaFile && (
          <div className={styles.mediaNotice}>
            Analyzing your attached {type === "story" ? "story" : "post"} media for visual cues.
          </div>
        )}

        {/* Custom Prompt Input */}
        <div className={styles.inputGroup}>
          <label>Give a prompt, mood, or idea (optional):</label>
          <textarea
            rows={3}
            placeholder="e.g. Sunset by the bay with good company, Fashion OOTD, Weekend vibes..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={loading || isLimitReached}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleGenerate();
              }
            }}
          />
        </div>

        {/* Tone Selector */}
        <div className={styles.toneContainer}>
          <span>Select Tone:</span>
          <div className={styles.tonePills}>
            {[
              "Trendy & Catchy",
              "Short & Punchy",
              "Aesthetic & Chill",
              "Luxury & Premium",
              "Humorous",
            ].map((t) => (
              <button
                key={t}
                className={`${styles.pill} ${
                  tone === t ? styles.activePill : ""
                }`}
                onClick={() => setTone(t)}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <button
          className={styles.generateBtn}
          onClick={handleGenerate}
          disabled={loading || isLimitReached}
        >
          {loading ? (
            "✨ Crafting Captions with Fameo AI..."
          ) : isLimitReached ? (
            `Limit Reached (Resets in ${countdownText})`
          ) : (
            <>
              <IoSparkles /> Generate with Fameo-X
            </>
          )}
        </button>

        {/* Results Area */}
        {generatedResults && (
          <div className={styles.resultsArea}>
            <h3>Suggested Captions:</h3>
            <div className={styles.captionList}>
              {generatedResults.captions?.map((c, idx) => {
                const cleanSentence = cleanCaptionSentence(c.text);
                return (
                  <div key={idx} className={styles.captionCard}>
                    <div className={styles.styleBadge}>{c.style}</div>
                    <p>{cleanSentence}</p>
                    <div className={styles.cardActions}>
                      <button
                        className={styles.useBtn}
                        onClick={() =>
                          handleSelectCaption(
                            cleanSentence,
                            (generatedResults.hashtags || []).map(t => cleanCaptionSentence(t).replace(/^#*/, '#'))
                          )
                        }
                      >
                        <FaCheck size={14} /> Use Caption
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {generatedResults.hashtags?.length > 0 && (
              <div className={styles.hashtagsRow}>
                <strong>Recommended Tags:</strong>{" "}
                {generatedResults.hashtags.map(t => cleanCaptionSentence(t).replace(/^#*/, '#')).join(" ")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AskFameoAssistantModal;